import { Fragment } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  ivaFrequencyForRegime,
  toggleTaxInList,
} from "@/lib/fiscalProfileEditorMappers";
import type { FiscalProfileEditorValues } from "@/lib/fiscalProfileEditorTypes";
import type { FiscalTaxType } from "@/services/fiscalService";

const TAX_OPTIONS: { value: FiscalTaxType; i18nKey: string }[] = [
  { value: "IVA", i18nKey: "taxIva" },
  { value: "IRAE", i18nKey: "taxIrae" },
  { value: "IRPF", i18nKey: "taxIrpf" },
  { value: "IRNR", i18nKey: "taxIrnr" },
  { value: "BPS", i18nKey: "taxBps" },
  { value: "MONOTRIBUTO", i18nKey: "taxMonotributo" },
];

/**
 * Monotributo is a cuota única sustitutiva: the single BPS aporte replaces
 * IVA, IRAE and the rentas taxes, so those boxes get cleared and locked while
 * it's ticked. BPS stays available — a monotributo *con dependientes* still
 * files aportes for its employees.
 */
const TAXES_REPLACED_BY_MONOTRIBUTO: FiscalTaxType[] = [
  "IVA",
  "IRAE",
  "IRPF",
  "IRNR",
];

/**
 * Literal E (IVA mínimo) IS the IRAE exoneration — art. 52 lit. E, renumbered
 * art. 66 Título 4. A tenant cannot be on IVA mínimo and also declare IRAE, so
 * ticking IVA mínimo clears it and locks the box.
 *
 * IVA is deliberately NOT in this list. The fixed cuota replaces the ordinary
 * débito−crédito calculation, but IVA must REMAIN in the saved taxes array:
 * the agenda derives the tenant's monthly IVA vencimientos from it, so removing
 * it would silently drop their obligations. It is locked and implied instead.
 */
const TAXES_REPLACED_BY_IVA_MINIMO: FiscalTaxType[] = ["IRAE"];

/** Monotributistas never present a balance — the cierre is always 31/12. */
const MONOTRIBUTO_FISCAL_YEAR_END = "12-31";

export interface TaxObligationsSectionProps {
  idPrefix: string;
  /** Defaults to 'owner'. 'accountant' swaps "tu" copy for third-person. */
  audience?: "owner" | "accountant";
}

export function TaxObligationsSection({
  idPrefix,
  audience = "owner",
}: TaxObligationsSectionProps): JSX.Element {
  const { t } = useTranslation();
  const { watch, setValue } = useFormContext<FiscalProfileEditorValues>();
  const taxes = watch("taxes");
  const ivaRegime = watch("ivaRegime");
  const audienceKey = audience === "accountant" ? "Acct" : "";

  const isMinimum = ivaRegime === "minimum";
  const isMonotributo = taxes.includes("MONOTRIBUTO");

  // Ticking Monotributo clears everything it substitutes (including the IVA
  // regime/benefit fields, which would otherwise stay behind as stale values)
  // and pins the cierre fiscal to 31/12. Unticking only unlocks the boxes —
  // it doesn't restore what was cleared, since we can't know what was there.
  const handleMonotributoToggle = (next: boolean): void => {
    if (!next) {
      setValue("taxes", toggleTaxInList(taxes, "MONOTRIBUTO"));
      return;
    }
    setValue("taxes", [
      ...taxes.filter((t) => !TAXES_REPLACED_BY_MONOTRIBUTO.includes(t)),
      "MONOTRIBUTO",
    ]);
    setValue("ivaRegime", "");
    setValue("ivaFrequency", "");
    setValue("ivaMinimumBenefit", "");
    setValue("fiscalBenefitOptIn", false);
    setValue("iraeFrequency", "");
    setValue("fiscalYearEnd", MONOTRIBUTO_FISCAL_YEAR_END);
  };

  // "IVA mínimo" is independent from the plain "IVA" checkbox: ticking it
  // only flips the regime (and the e-Factura benefit default), so the user
  // controls each box explicitly. Persistence layer ensures IVA ends up in
  // the saved taxes array whenever regime='minimum', so the agenda still
  // gets monthly IVA obligations either way.
  const handleMinimumToggle = (next: boolean): void => {
    if (next) {
      setValue("ivaRegime", "minimum");
      setValue("ivaFrequency", ivaFrequencyForRegime("minimum"));
      // Literal E always gets the e-Factura benefit — no standard/efactura choice.
      setValue("ivaMinimumBenefit", "efactura_3_3");
      // Drop what Literal E exonerates (IRAE) and pin IVA on. IVA has to stay in
      // the array for the agenda to emit monthly vencimientos — see the constant
      // above. Unticking later only unlocks the boxes; it can't restore what was
      // cleared, since we don't know what was there.
      setValue("taxes", [
        ...taxes.filter((t) => !TAXES_REPLACED_BY_IVA_MINIMO.includes(t)),
        ...(taxes.includes("IVA") ? [] : (["IVA"] as FiscalTaxType[])),
      ]);
    } else {
      setValue("ivaRegime", "no_cede");
      setValue("ivaFrequency", ivaFrequencyForRegime("no_cede"));
      setValue("ivaMinimumBenefit", "");
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-foreground">
          {t(
            `settings.fiscalProfile.sections.taxObligationsTitle${audienceKey}`,
          )}
        </CardTitle>
        <CardDescription>
          {t(
            `settings.fiscalProfile.sections.taxObligationsDescription${audienceKey}`,
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        <Label className="text-foreground">{t("settings.fiscalProfile.taxes")}</Label>
        <div className="flex flex-wrap gap-4">
          {TAX_OPTIONS.map(({ value, i18nKey }) => {
            const isMonotributoOption = value === "MONOTRIBUTO";
            // IVA is locked-but-checked under IVA mínimo (implied, and required
            // by the agenda); IRAE is locked-and-cleared (Literal E exonerates it).
            const lockedByMinimo =
              isMinimum &&
              (value === "IVA" || TAXES_REPLACED_BY_IVA_MINIMO.includes(value));
            const locked =
              (isMonotributo && TAXES_REPLACED_BY_MONOTRIBUTO.includes(value)) ||
              lockedByMinimo;
            return (
              <Fragment key={value}>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id={`${idPrefix}-tax-${value}`}
                    checked={taxes.includes(value)}
                    disabled={locked}
                    onCheckedChange={(v) =>
                      isMonotributoOption
                        ? handleMonotributoToggle(v === true)
                        : setValue("taxes", toggleTaxInList(taxes, value))
                    }
                    className="transition-colors duration-150"
                  />
                  <Label
                    htmlFor={`${idPrefix}-tax-${value}`}
                    className={
                      locked
                        ? "text-sm font-normal text-muted-foreground"
                        : "cursor-pointer text-sm font-normal"
                    }
                  >
                    {t(`settings.fiscalProfile.${i18nKey}`)}
                  </Label>
                </div>
                {value === "IVA" && (
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id={`${idPrefix}-tax-iva-minimum`}
                      checked={isMinimum}
                      disabled={isMonotributo}
                      onCheckedChange={(v) => handleMinimumToggle(v === true)}
                      className="transition-colors duration-150"
                    />
                    <Label
                      htmlFor={`${idPrefix}-tax-iva-minimum`}
                      className={
                        isMonotributo
                          ? "text-sm font-normal text-muted-foreground"
                          : "cursor-pointer text-sm font-normal"
                      }
                    >
                      {t("settings.fiscalProfile.taxIvaMinimum", "IVA mínimo")}
                    </Label>
                  </div>
                )}
              </Fragment>
            );
          })}
        </div>
        {isMinimum && !isMonotributo && (
          <p className="text-xs text-muted-foreground">
            {t(
              "settings.fiscalProfile.ivaMinimoReplacesOthers",
              "El régimen de IVA mínimo incluye el IVA y exonera el IRAE (exoneración Literal E), así que esos impuestos no se eligen por separado.",
            )}
          </p>
        )}
        {isMonotributo && (
          <p className="text-xs text-muted-foreground">
            {t(
              "settings.fiscalProfile.monotributoReplacesOthers",
              "El monotributo es una cuota única que sustituye IVA, IRAE e impuestos a la renta. El cierre fiscal queda fijado al 31/12.",
            )}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
